// Comparaison de deux contenus de version : liste de changements en mots (ajouté, modifié,
// supprimé), par zone de l'éditeur. Sert au résumé affiché avant la publication et à l'écran de
// comparaison des versions. Fonctions pures, sans dépendance à l'API.
import { LIBELLES_TYPES_CHAMP, STATUTS_USAGER, tousLesChamps, type ContenuVersion, type StatutUsager } from '@cc/contrats';
import { libelleDelai, LIBELLES_CANAUX, LIBELLES_NATURE_ETAPE } from './contenu';

export type NatureChangement = 'ajout' | 'modification' | 'suppression';
export type ZoneChangement = 'identite' | 'conditions' | 'formulaire' | 'pieces' | 'etapes' | 'delais' | 'tarif' | 'document' | 'textes';

export interface Changement {
  nature: NatureChangement;
  zone: ZoneChangement;
  texte: string;
}

export const LIBELLES_ZONES: Record<ZoneChangement, string> = {
  identite: 'Identité et fondement',
  conditions: 'Conditions',
  formulaire: 'Formulaire',
  pieces: 'Pièces',
  etapes: 'Étapes',
  delais: 'Délais',
  tarif: 'Tarif',
  document: 'Document final et conservation',
  textes: 'Textes usager',
};

export const LIBELLES_NATURE: Record<NatureChangement, string> = { ajout: 'Ajouté', modification: 'Modifié', suppression: 'Supprimé' };

const identique = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const texteOuVide = (v: unknown) => (v === null || v === undefined || v === '' ? 'non renseigné' : String(v));

function champSimple(liste: Changement[], zone: ZoneChangement, libelle: string, avant: unknown, apres: unknown, formater: (v: unknown) => string = texteOuVide) {
  if (identique(avant, apres)) return;
  liste.push({ nature: 'modification', zone, texte: `${libelle} : « ${formater(avant)} » devient « ${formater(apres)} »` });
}

function comparerFormulaire(liste: Changement[], a: ContenuVersion, b: ContenuVersion) {
  const sectionsA = new Map(a.formulaire.sections.map((s) => [s.code, s]));
  const sectionsB = new Map(b.formulaire.sections.map((s) => [s.code, s]));
  for (const [code, s] of sectionsB) {
    const avant = sectionsA.get(code);
    if (!avant) liste.push({ nature: 'ajout', zone: 'formulaire', texte: `Section « ${s.titre} » (${s.champs.length} champ${s.champs.length > 1 ? 's' : ''})` });
    else if (avant.titre !== s.titre) liste.push({ nature: 'modification', zone: 'formulaire', texte: `Section « ${avant.titre} » renommée « ${s.titre} »` });
    if (avant && !identique(avant.visible_si, s.visible_si)) liste.push({ nature: 'modification', zone: 'formulaire', texte: `Condition d'affichage de la section « ${s.titre} »` });
  }
  for (const [code, s] of sectionsA) if (!sectionsB.has(code)) liste.push({ nature: 'suppression', zone: 'formulaire', texte: `Section « ${s.titre} » et ses champs` });

  const champsA = new Map(tousLesChamps(a.formulaire).map((x) => [x.champ.code, x]));
  const champsB = new Map(tousLesChamps(b.formulaire).map((x) => [x.champ.code, x]));
  for (const [code, x] of champsB) {
    const avant = champsA.get(code);
    const nom = `Champ « ${x.champ.libelle} » (${LIBELLES_TYPES_CHAMP[x.champ.type]})`;
    if (!avant) {
      liste.push({ nature: 'ajout', zone: 'formulaire', texte: `${nom}${x.champ.visible_si ? ', conditionnel' : ''}${x.champ.obligatoire ? ', obligatoire' : ''}` });
      continue;
    }
    const details: string[] = [];
    if (avant.champ.libelle !== x.champ.libelle) details.push(`libellé « ${avant.champ.libelle} » devient « ${x.champ.libelle} »`);
    if (avant.champ.type !== x.champ.type) details.push(`type ${LIBELLES_TYPES_CHAMP[avant.champ.type]} devient ${LIBELLES_TYPES_CHAMP[x.champ.type]}`);
    if (avant.champ.obligatoire !== x.champ.obligatoire) details.push(x.champ.obligatoire ? 'rendu obligatoire' : 'rendu facultatif');
    if (!identique(avant.champ.visible_si, x.champ.visible_si)) details.push(x.champ.visible_si ? 'condition d\'affichage modifiée' : 'condition d\'affichage retirée');
    if (!identique(avant.champ.options, x.champ.options)) details.push('options modifiées');
    if (!identique(avant.champ.validation, x.champ.validation)) details.push('règles de validation modifiées');
    if (!identique(avant.champ.calcul, x.champ.calcul)) details.push('calcul modifié');
    if (!identique(avant.champ.colonnes, x.champ.colonnes)) details.push('colonnes modifiées');
    if ((avant.champ.aide ?? '') !== (x.champ.aide ?? '')) details.push('aide modifiée');
    if (avant.section.code !== x.section.code) details.push(`déplacé vers la section « ${x.section.titre} »`);
    if (details.length > 0) liste.push({ nature: 'modification', zone: 'formulaire', texte: `${nom} : ${details.join(', ')}` });
  }
  for (const [code, x] of champsA) if (!champsB.has(code)) liste.push({ nature: 'suppression', zone: 'formulaire', texte: `Champ « ${x.champ.libelle} »` });
}

function comparerPieces(liste: Changement[], a: ContenuVersion, b: ContenuVersion) {
  const avant = new Map(a.pieces.map((p) => [p.type, p]));
  const apres = new Map(b.pieces.map((p) => [p.type, p]));
  for (const [type, p] of apres) {
    const x = avant.get(type);
    if (!x) {
      liste.push({ nature: 'ajout', zone: 'pieces', texte: `Pièce « ${p.libelle} »${p.obligatoire ? ', obligatoire' : ', facultative'}${p.condition ? ', conditionnelle' : ''}` });
      continue;
    }
    const details: string[] = [];
    if (x.libelle !== p.libelle) details.push(`libellé « ${x.libelle} » devient « ${p.libelle} »`);
    if (x.obligatoire !== p.obligatoire) details.push(p.obligatoire ? 'rendue obligatoire' : 'rendue facultative');
    if (!identique(x.condition, p.condition)) details.push(p.condition ? 'condition modifiée' : 'condition retirée');
    if ((x.aide ?? '') !== (p.aide ?? '')) details.push('aide modifiée');
    if (details.length > 0) liste.push({ nature: 'modification', zone: 'pieces', texte: `Pièce « ${p.libelle} » : ${details.join(', ')}` });
  }
  for (const [type, p] of avant) if (!apres.has(type)) liste.push({ nature: 'suppression', zone: 'pieces', texte: `Pièce « ${p.libelle} »` });
}

function comparerEtapes(liste: Changement[], a: ContenuVersion, b: ContenuVersion, services: Map<string, string>) {
  const nom = (id: string | null) => (id ? (services.get(id) ?? `service ${id.slice(0, 8)}`) : 'sans service');
  const avant = new Map(a.etapes.map((e, i) => [e.code, { e, i }]));
  const apres = new Map(b.etapes.map((e, i) => [e.code, { e, i }]));
  for (const [code, { e, i }] of apres) {
    const x = avant.get(code);
    if (!x) {
      liste.push({ nature: 'ajout', zone: 'etapes', texte: `Étape ${i + 1} « ${e.libelle} » (${LIBELLES_NATURE_ETAPE[e.nature]}, ${nom(e.service_id)})` });
      continue;
    }
    const details: string[] = [];
    if (x.e.libelle !== e.libelle) details.push(`libellé « ${x.e.libelle} » devient « ${e.libelle} »`);
    if (x.e.nature !== e.nature) details.push(`nature ${LIBELLES_NATURE_ETAPE[x.e.nature]} devient ${LIBELLES_NATURE_ETAPE[e.nature]}`);
    if (x.e.service_id !== e.service_id) details.push(`service ${nom(x.e.service_id)} devient ${nom(e.service_id)}`);
    if (x.e.delai_interne_jours !== e.delai_interne_jours) details.push(`délai interne ${texteOuVide(x.e.delai_interne_jours)} devient ${texteOuVide(e.delai_interne_jours)} jours`);
    if (x.e.role_code !== e.role_code) details.push('rôle attendu modifié');
    if (!identique(x.e.actions, e.actions)) details.push('actions modifiées');
    if (x.i !== i) details.push(`rang ${x.i + 1} devient ${i + 1}`);
    if (details.length > 0) liste.push({ nature: 'modification', zone: 'etapes', texte: `Étape « ${e.libelle} » : ${details.join(', ')}` });
  }
  for (const [code, { e }] of avant) {
    if (!apres.has(code)) liste.push({ nature: 'suppression', zone: 'etapes', texte: `Étape « ${e.libelle} » : les dossiers arrivés à cette étape ne pourront pas être migrés` });
  }
}

/** Changements de « avant » vers « apres ». Les services sont nommés quand la carte est fournie. */
export function comparerContenus(avant: ContenuVersion, apres: ContenuVersion, services: Map<string, string> = new Map()): Changement[] {
  const liste: Changement[] = [];
  champSimple(liste, 'identite', 'Libellé', avant.libelle, apres.libelle);
  champSimple(liste, 'identite', 'Description', avant.description, apres.description);
  champSimple(liste, 'identite', 'Domaine', avant.domaine, apres.domaine);
  champSimple(liste, 'identite', 'Autorité responsable', avant.autorite_responsable, apres.autorite_responsable);
  champSimple(liste, 'identite', 'Fondement', avant.fondement, apres.fondement);
  champSimple(liste, 'conditions', 'Texte des conditions', avant.conditions_eligibilite.texte, apres.conditions_eligibilite.texte);
  if (!identique(avant.conditions_eligibilite.regles, apres.conditions_eligibilite.regles)) {
    const n1 = avant.conditions_eligibilite.regles.length;
    const n2 = apres.conditions_eligibilite.regles.length;
    liste.push({ nature: n2 > n1 ? 'ajout' : n2 < n1 ? 'suppression' : 'modification', zone: 'conditions', texte: `Règles d'éligibilité : ${n1} règle${n1 > 1 ? 's' : ''} avant, ${n2} après` });
  }
  comparerFormulaire(liste, avant, apres);
  comparerPieces(liste, avant, apres);
  comparerEtapes(liste, avant, apres, services);
  champSimple(liste, 'delais', 'Délai réglementaire', avant.delais.reglementaire, apres.delais.reglementaire, (v) => libelleDelai(v as ContenuVersion['delais']['reglementaire']));
  champSimple(liste, 'delais', 'Délai interne', avant.delais.interne, apres.delais.interne, (v) => libelleDelai(v as ContenuVersion['delais']['interne']));
  champSimple(liste, 'delais', 'Relance de complément (jours)', avant.delais.relance_complement_jours, apres.delais.relance_complement_jours);
  champSimple(liste, 'delais', 'Classement sans suite (jours)', avant.delais.classement_sans_suite_jours, apres.delais.classement_sans_suite_jours);
  champSimple(liste, 'tarif', 'Rubrique tarifaire', avant.tarif.rubrique, apres.tarif.rubrique);
  champSimple(liste, 'tarif', 'Montant indicatif (francs CFA)', avant.tarif.montant_indicatif, apres.tarif.montant_indicatif);
  champSimple(liste, 'tarif', 'Moment du paiement', avant.tarif.moment_paiement, apres.tarif.moment_paiement);
  champSimple(liste, 'document', 'Modèle du document final', avant.modele_document_code, apres.modele_document_code);
  champSimple(liste, 'document', 'Règle de conservation', avant.regle_conservation_id, apres.regle_conservation_id);
  champSimple(liste, 'document', 'Identité vérifiée requise', avant.identite_verifiee_requise, apres.identite_verifiee_requise, (v) => (v ? 'oui' : 'non'));
  champSimple(liste, 'document', 'Canaux de notification', avant.canaux_notification, apres.canaux_notification, (v) => ((v as string[]) ?? []).map((c) => LIBELLES_CANAUX[c] ?? c).join(', ') || 'aucun');
  for (const statut of Object.keys(STATUTS_USAGER) as StatutUsager[]) {
    champSimple(liste, 'textes', `Texte du statut « ${STATUTS_USAGER[statut]} »`, avant.textes_statuts[statut], apres.textes_statuts[statut]);
  }
  return liste;
}

/** Codes des étapes supprimées : un dossier déjà arrivé à l'une d'elles ne pourra pas être migré. */
export function etapesSupprimees(avant: ContenuVersion, apres: ContenuVersion): string[] {
  const codes = new Set(apres.etapes.map((e) => e.code));
  return avant.etapes.filter((e) => !codes.has(e.code)).map((e) => e.libelle);
}
